import {
  Button,
  Dimmer,
  DimmerDimmable,
  Form,
  Loader,
  Modal,
  Segment,
} from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import { ModeReglementContext } from '@/contexts';
import type { DataReglementType, FactureType } from '@/types';
import { avecCodeModeReglement, estLigneEligible } from '@/utils';

import ModalAffectationReglementTable from './ModalAffectationReglementTable';

export interface ModalAffectationReglementProps {
  loading: boolean;
  reglements: (DataReglementType & { complet?: boolean })[];
  factures: FactureType[];
  montant: number;
  onClose: () => void;
  onValidate: (montantRegle: Record<string, number | string>[]) => void;
}

const ModalAffectationReglement = (props: ModalAffectationReglementProps) => {
  const { loading, reglements, factures, montant, onClose, onValidate } = props;

  // #region Declaration

  // État contenant les informations de règlements sur les différents lignes de facture.
  const [montantRegle, setMontantRegle] = React.useState<Record<string, number | string>[]>([]);

  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Règlements complétés du code de leur mode, pour réserver les titres aux produits éligibles.
  const reglementsAvecCode = React.useMemo(
    () => avecCodeModeReglement(reglements, regieModesReglement),
    [reglements, regieModesReglement],
  );

  /**
   * Vrai si la cellule croise un titre de paiement (CESU, chèque vacances) et une ligne dont le
   * produit n'y est pas éligible : elle ne peut rien recevoir (REG-862).
   */
  const estAffectationNonEligible = React.useCallback(
    (identifiantFacture, identifiantLigne, identifiantReglement) => {
      const ligne = _.find(_.find(factures, { identifiant: identifiantFacture })?.lignes, {
        identifiant: identifiantLigne,
      });
      const code = _.find(reglementsAvecCode, { identifiant: identifiantReglement })?.code;

      return !estLigneEligible(ligne ?? {}, code);
    },
    [factures, reglementsAvecCode],
  );

  // #endregion

  // #region Fonctions

  /**
   * Gère la validation de l'affectation des montants.
   */
  const handleValidate = React.useCallback(() => {
    if (onValidate) onValidate(montantRegle);
  }, [montantRegle, onValidate]);

  /**
   * Met à jour le montant affecté à une ligne de facture spécifique.
   *
   * @param {number} identifiantFacture - L'identifiant de la facture.
   * @param {number} identifiantLigne - L'identifiant de la ligne de facture.
   * @param {string} identifiantReglement - L'identifiant du règlement.
   * @param {string} value - La valeur du montant à affecter.
   */
  const handleChangeMontant = React.useCallback(
    (
      identifiantFacture: number,
      identifiantLigne: number,
      identifiantReglement: string | number,
      value: string,
    ) => {
      setMontantRegle((prev) => {
        const res = _.cloneDeep(prev);
        const index = _.findIndex(res, {
          identifiantFacture,
          identifiantLigne,
          identifiantReglement,
        });
        res[index].montant = value;
        return res;
      });
    },
    [],
  );

  // Gère le focus des champs quand on appui sur entrée.
  const handleKeyPress = (e) => {
    const {
      key,
      target: { id },
    } = e;

    const indice = parseInt(id.split('@')[1], 10);

    let nextIndice = indice;

    const nombreLignesTotal = _.sumBy(factures, (f) => f.lignes.length);

    let done = false;

    if (key === '+') {
      setMontantRegle((prev) =>
        _.map(prev, (mr) => {
          if (
            mr.enterIndex === indice &&
            !estAffectationNonEligible(
              mr.identifiantFacture,
              mr.identifiantLigne,
              mr.identifiantReglement,
            )
          ) {
            const reglement = _.find(
              reglements,
              (r) => r.identifiant === mr.identifiantReglement,
            )?.montant;

            const ligne = _.find(
              _.find(factures, (f) => f.identifiant === mr.identifiantFacture).lignes,
              (l) => l.identifiant === mr.identifiantLigne,
            )?.reste;

            const montantReglement = _.sumBy(
              _.filter(
                prev,
                (a) =>
                  a.identifiantReglement === mr.identifiantReglement &&
                  a.enterIndex !== mr.enterIndex,
              ),
              (m) => (m.montant as number) || 0,
            );

            const montantLigne = _.sumBy(
              _.filter(
                prev,
                (a) =>
                  a.identifiantLigne === mr.identifiantLigne &&
                  a.identifiantFacture === mr.identifiantFacture &&
                  a.enterIndex !== mr.enterIndex,
              ),
              (m) => (m.montant as number) || 0,
            );

            let valeur = 0;
            if (montantReglement === 0) {
              if (ligne - montantLigne === 0) {
                valeur = 0;
              } else {
                valeur = reglement < ligne - montantLigne ? reglement : ligne - montantLigne;
              }
            } else {
              valeur =
                reglement - montantReglement > ligne - montantLigne
                  ? ligne - montantLigne
                  : reglement - montantReglement;
            }

            if (montantReglement - reglement - valeur === 0) {
              done = true;
            }

            return {
              ...mr,
              montant: valeur <= 0 ? '' : parseFloat(valeur.toFixed(2)),
            };
          }

          return mr;
        }),
      );

      e.preventDefault();
    }

    if (key === 'Enter') {
      if (done) {
        nextIndice = e.shiftKey ? indice - 2 : indice + 2;
      } else {
        nextIndice = e.shiftKey ? indice - 1 : indice + 1;
      }
    }

    if (key === 'ArrowUp') {
      nextIndice = indice - 1;
    }

    if (key === 'ArrowDown') {
      nextIndice = indice + 1;
    }

    if (key === 'ArrowLeft') {
      nextIndice = indice - nombreLignesTotal;
    }

    if (key === 'ArrowRight') {
      nextIndice = indice + nombreLignesTotal;
    }

    if (nextIndice >= 0) {
      const pas = nextIndice - indice;
      let nextElement = document.getElementById(`currency@${nextIndice}`) as HTMLInputElement;

      // Les cellules non éligibles sont désactivées : on les saute dans le sens du déplacement.
      while (pas !== 0 && nextElement?.disabled) {
        nextIndice += pas;
        nextElement = document.getElementById(`currency@${nextIndice}`) as HTMLInputElement;
      }

      if (nextElement) {
        nextElement.focus();
      }
    }
  };

  /**
   * On vérifie que dans les lignes de montant réglé, le montant total réglé
   * pour une ligne de facture et inférieur ou égal au reste à encaisser de la ligne.
   *
   * @returns {boolean} - True si il y a une erreur dans une des lignes.
   */
  const isLigneFactureError = React.useMemo(
    () =>
      _.some(
        // groupe par facture
        _.groupBy(montantRegle, 'identifiantFacture'),
        (facturesGrouped) =>
          _.some(
            // groupe par ligne
            _.groupBy(facturesGrouped, 'identifiantLigne'),
            (lignesGrouped) => {
              // récupération du montant total réglé pour une ligne
              const montantTotalLigneReglement = parseFloat(
                _.sumBy(lignesGrouped, (ligne) => (ligne.montant as number) || 0).toFixed(2),
              );

              // récupération de la facture
              const facture = _.find(factures, {
                identifiant: _.first(lignesGrouped)?.identifiantFacture,
              });

              // récupération de la ligne
              const ligne = _.find(
                facture?.lignes,
                (l) => _.first(lignesGrouped)?.identifiantLigne === l.identifiant,
              );

              return montantTotalLigneReglement > (ligne?.reste ?? 0);
            },
          ),
      ),
    [factures, montantRegle],
  );

  /**
   * On vérifie que dans les lignes de montant réglé, le montant total d'un règlement
   * et inférieur ou égal au montant total possible du règlement.
   *
   * @returns {boolean} - True si il y a une erreur dans un des règlements.
   */
  const isReglementError = React.useMemo(
    () =>
      // groupe par reglement
      _.some(_.groupBy(montantRegle, 'identifiantReglement'), (reglement) => {
        // récupération du montant total réglé pour un règlement
        const montantTotalReglement = parseFloat(
          _.sumBy(reglement, (r) => (r.montant as number) || 0).toFixed(2),
        );

        // récupération du règlement
        const reg = _.find(reglements, { identifiant: _.first(reglement)?.identifiantReglement });

        return montantTotalReglement > reg.montant;
      }),
    [montantRegle, reglements],
  );

  // Vrai si un titre de paiement est affecté à un produit qui n'y est pas éligible.
  const isAffectationNonEligibleError = React.useMemo(
    () =>
      _.some(
        montantRegle,
        (mr) =>
          (mr.montant as number) > 0 &&
          estAffectationNonEligible(
            mr.identifiantFacture,
            mr.identifiantLigne,
            mr.identifiantReglement,
          ),
      ),
    [estAffectationNonEligible, montantRegle],
  );

  // #endregion

  // #region Effets

  // Initialise l'état montantRegle en fonction des factures.
  React.useEffect(() => {
    const res = [];
    // Ajout d'un indice pour pouvoir gérer la touche entrée
    let enterIndex = 1;

    _.forEach(reglements, (reglement) => {
      _.forEach(factures, (facture) => {
        _.forEach(facture.lignes, (ligne) => {
          res.push({
            enterIndex,
            identifiantFacture: facture.identifiant,
            identifiantLigne: ligne.identifiant,
            identifiantReglement: reglement.identifiant,
            montant: '',
          });

          enterIndex += 1;
        });
      });
    });

    let tabIndex = 1;

    _.forEach(factures, (facture) => {
      _.forEach(facture.lignes, (ligne) => {
        _.forEach(reglements, (reglement) => {
          const index = _.findIndex(res, {
            identifiantFacture: facture.identifiant,
            identifiantLigne: ligne.identifiant,
            identifiantReglement: reglement.identifiant,
          });

          res[index].tabIndex = tabIndex;

          tabIndex += 1;
        });
      });
    });

    setMontantRegle(res);
  }, [factures, reglements]);

  // #endregion

  return (
    <Modal open closeIcon {...(!loading && { onClose })} size="big">
      <Modal.Header>Répartition du règlement</Modal.Header>
      <Modal.Content>
        <Segment
          style={{
            padding: 0,
            maxHeight: 350,
            scrollPaddingBottom: 100,
            scrollPaddingTop: 80,
            overflowY: 'auto',
          }}
        >
          <DimmerDimmable as={Form} dimmed={loading}>
            <Dimmer active={loading} inverted>
              <Loader inverted content="Encaissement de(s) facture(s) en cours" />
            </Dimmer>
            <ModalAffectationReglementTable
              montant={montant}
              factures={factures}
              reglements={reglementsAvecCode}
              montantRegle={montantRegle}
              onKeyPress={handleKeyPress}
              onChangeMontant={handleChangeMontant}
            />
          </DimmerDimmable>
        </Segment>
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          size="tiny"
          content="Annuler"
          onClick={onClose}
          icon="close"
          disabled={loading}
          loading={loading}
        />
        <Button
          data-testid="button-validate-affectation"
          positive
          content="Valider"
          onClick={handleValidate}
          disabled={
            parseFloat(_.sumBy(montantRegle, (m) => (m.montant as number) || 0).toFixed(2)) !==
              montant ||
            isReglementError ||
            isLigneFactureError ||
            isAffectationNonEligibleError ||
            loading
          }
          loading={loading}
          icon="checkmark"
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalAffectationReglement;
